import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, GripVertical, RotateCcw, Loader2, Plus, Archive, Trash2, Undo2 } from "lucide-react";

/**
 * Editable table of PM lifecycle stages — labels, order, SLA (days),
 * and per-stage approver role. Admins/managers only. Drag rows to reorder.
 */
export default function PmStageConfigSection() {
  const [stages, setStages] = useState(null);
  const [roleKeys, setRoleKeys] = useState([]);   // [{key,label}]
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    try {
      const [cfg, roles] = await Promise.all([
        api.get("/pm/stage-config"),
        api.get("/pm/roles"),
      ]);
      setStages(cfg.data.stages);
      setRoleKeys(roles.data.role_keys || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load PM stages");
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (idx, field, value) => {
    setStages(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const moveRow = (from, to) => {
    if (from === to || from == null || to == null) return;
    setStages(prev => {
      const next = [...prev];
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row);
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const addStage = () => {
    setStages(prev => [
      ...(prev || []),
      {
        key: "",   // let the server auto-slug from label
        label: "New Stage",
        order: prev.length,
        sla_days: null,
        approver_role_key: roleKeys[0]?.key || "pm",
        archived: false,
        _isNew: true,
      },
    ]);
  };

  const removeRow = (idx) => {
    setStages(prev => {
      const s = prev[idx];
      // Server-persisted rows cannot be deleted (they'd break historical
      // audits) — set archived=true instead. New (unsaved) rows can be
      // dropped outright.
      if (!s?._isNew) return prev;
      const next = [...prev];
      next.splice(idx, 1);
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  const toggleArchive = (idx) => {
    setStages(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], archived: !next[idx].archived };
      return next;
    });
  };

  const save = async () => {
    if (!stages) return;
    setSaving(true);
    try {
      const payload = {
        stages: stages.map((s, i) => ({
          key: s.key || undefined,   // undefined lets the server slug from label
          label: s.label,
          order: i,
          sla_days: s.sla_days === "" || s.sla_days == null ? null : Number(s.sla_days),
          approver_role_key: s.approver_role_key || null,
          archived: !!s.archived,
        })),
      };
      const { data } = await api.put("/pm/stage-config", payload);
      setStages(data.stages);
      toast.success("Lifecycle stages saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save stages");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset every stage back to the built-in labels and clear all SLAs?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/pm/stage-config/reset");
      setStages(data.stages);
      toast.success("Stages reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reset stages");
    } finally {
      setSaving(false);
    }
  };

  if (!stages) {
    return (
      <section className="swiss-card-flat p-6 mb-6" data-testid="pm-stage-config-section">
        <div className="muted text-xs">Loading PM stages…</div>
      </section>
    );
  }

  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="pm-stage-config-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <div className="label">Project Management · Lifecycle stages</div>
          <div className="muted text-xs mt-1">
            Rename stages, reorder the workflow (drag the handle), set a target SLA
            in days per stage, and pick which role approves completion. Stage keys
            stay stable so historical audits remain intact.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-ghost text-xs"
            onClick={reset}
            disabled={saving}
            data-testid="pm-stage-reset-btn"
          >
            <RotateCcw size={12} /> Reset
          </button>
          <button
            className="btn-accent text-xs"
            onClick={save}
            disabled={saving}
            data-testid="pm-stage-save-btn"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save stages
          </button>
        </div>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="data-table text-sm" data-testid="pm-stage-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th className="w-8">#</th>
              <th>Label</th>
              <th>Approver role</th>
              <th className="text-right">SLA (days)</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s, idx) => (
              <tr
                key={s.key || `new-${idx}`}
                data-testid={`pm-stage-row-${s.key || `new-${idx}`}`}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => { moveRow(dragIndex, idx); setDragIndex(null); }}
                className={
                  (dragIndex === idx ? "opacity-40 " : "") +
                  (s.archived ? "bg-[var(--surface-2,#F6F7F9)] opacity-70" : "")
                }
              >
                <td
                  className="cursor-grab active:cursor-grabbing text-center"
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragEnd={() => setDragIndex(null)}
                  data-testid={`pm-stage-drag-${s.key || `new-${idx}`}`}
                >
                  <GripVertical size={14} className="muted mx-auto" />
                </td>
                <td className="font-mono text-xs muted">{idx + 1}</td>
                <td>
                  <input
                    className="input-swiss w-full"
                    value={s.label || ""}
                    onChange={(e) => setField(idx, "label", e.target.value)}
                    placeholder="Stage label"
                    data-testid={`pm-stage-label-${s.key || `new-${idx}`}`}
                  />
                  <div className="text-[10px] font-mono muted mt-0.5 flex items-center gap-1 flex-wrap">
                    <span>key: {s.key || <em className="italic">auto (from label on save)</em>}</span>
                    {s._isNew && <span className="tag" style={{ background: "#DDF3F1", color: "#0F7A73" }}>new</span>}
                    {s.archived && <span className="tag" style={{ background: "#F7DCEC", color: "#8A5A2C" }}>archived</span>}
                  </div>
                </td>
                <td>
                  <select
                    className="input-swiss w-full"
                    value={s.approver_role_key || ""}
                    onChange={(e) => setField(idx, "approver_role_key", e.target.value)}
                    data-testid={`pm-stage-approver-${s.key || `new-${idx}`}`}
                  >
                    {roleKeys.map(rk => (
                      <option key={rk.key} value={rk.key}>{rk.label}</option>
                    ))}
                  </select>
                </td>
                <td className="text-right">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="input-swiss text-right w-24"
                    value={s.sla_days == null ? "" : s.sla_days}
                    onChange={(e) => setField(idx, "sla_days", e.target.value === "" ? null : Number(e.target.value))}
                    placeholder="—"
                    data-testid={`pm-stage-sla-${s.key || `new-${idx}`}`}
                  />
                </td>
                <td>
                  <div className="flex gap-1 justify-end">
                    {s._isNew ? (
                      <button
                        onClick={() => removeRow(idx)}
                        className="btn-ghost !p-1.5 text-red-600"
                        title="Remove this new stage"
                        data-testid={`pm-stage-remove-${idx}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <button
                        onClick={() => toggleArchive(idx)}
                        className="btn-ghost !p-1.5"
                        title={s.archived ? "Restore this stage" : "Archive (hide from board)"}
                        data-testid={`pm-stage-archive-${s.key}`}
                      >
                        {s.archived ? <Undo2 size={12} /> : <Archive size={12} />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={addStage}
          className="btn-ghost text-xs"
          data-testid="pm-stage-add-btn"
        >
          <Plus size={12} /> Add stage
        </button>
        <div className="muted text-[11px]">
          Archive keeps historical projects intact; new stages get an auto-slug key from the label on save.
        </div>
      </div>
    </section>
  );
}
